import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useLocalSessionId,
  useParticipantIds,
  useParticipantProperty,
  useScreenShare,
} from '@daily-co/daily-react';
import { useSubjectLanguageVariants } from '@features/user';
import { PipExitIcon } from '@jotpsych/ui/icons/PipExitIcon';
import { PipIcon } from '@jotpsych/ui/icons/PipIcon';
import {
  ArrowBack as ArrowBackIcon,
  ChatBubbleOutlined as ChatIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Logout as LogoutIcon,
  Mic as MicIcon,
  MicOff as MicOffIcon,
  PeopleAlt as PeopleIcon,
  ScreenShare as ScreenShareIcon,
  StopScreenShare as StopScreenShareIcon,
  Videocam as VideocamIcon,
  VideocamOff as VideocamOffIcon,
} from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import type { AudioInputDeviceService } from '@services/audio';
import type { DailyCallService } from '@services/dailycall';
import { useDailyCallState } from '@services/dailycall';
import SentryService from '@services/SentryService';
import { useEventTracking } from '@services/statsig';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useSnackbar } from 'notistack';

import { useTelehealthCallManager } from '../../context/useTelehealthCallManager';
import { useCallLayout } from '../../hooks/useCallLayout';
import {
  formatTelehealthParticipantList,
  useTelehealthCallLifecycleGuard,
} from '../../hooks/useTelehealthCallLifecycleGuard';
import {
  clearWaitingRoomLaunchIntent,
  selectChatMessages,
  selectChatUnreadCount,
  selectHideSelfView,
  selectIsChatPanelOpen,
  selectWaitingRoomLaunchIntent,
  setChatPanelOpen,
  toggleChatPanel,
} from '../../store';
import type { ManualLayoutChoice, TelehealthSession } from '../../types';
import type { ProviderWaitingRoomRemoveRequest, WaitingRoomEntry } from '../../waiting-room';
import {
  getWaitingRoomAdmitErrorMessage,
  getWaitingRoomEntrySublabel,
  getWaitingRoomRemoveErrorMessage,
  isAdmittableEntry,
  isInCallWaitingRoomEntry,
  isWaitingRoomListEntry,
  WaitingRoomEntryActionButtons,
  WaitingRoomPeoplePanel,
  WaitingRoomRemoveDialog,
} from '../../waiting-room';
import { CameraDropdown } from '../CameraDropdown';
import { ChatPanel } from '../ChatPanel';
import { GridView } from '../GridView';
import { LayoutToggle } from '../LayoutToggle';
import { MicDropdown } from '../MicDropdown';
import { RecordingStatusBar } from '../RecordingStatusBar';
import { SpeakerView } from '../SpeakerView';
import { SplitMediaButton } from '../SplitMediaButton';
import { VideoSpotlight } from '../VideoSpotlight';

interface PipViewProps {
  dailyCallService: DailyCallService;
  audioInputDeviceService: AudioInputDeviceService | null;
  setSelectedMicrophone: (mic: MediaDeviceInfo) => void;
  pipWindow?: Window;
  isRecording: boolean;
  isRecordingStarting?: boolean;
  isRecordingPaused?: boolean;
  session: TelehealthSession;
  onLeave: () => void;
  onClose?: () => void;
  isPipWindow?: boolean;
  onEnterPip?: () => void;
  isPipSupported?: boolean;
  onPauseRecording?: () => void;
  onResumeRecording?: () => void;
  onToggleFullscreen?: () => void;
  isFullscreen?: boolean;
  compactControls?: boolean;
  /** Container element for popovers — needed in fullscreen mode where portals to document.body are hidden */
  popoverContainerEl?: HTMLElement | null;
}

const getAdmitActionLabel = (entry: WaitingRoomEntry) => {
  if (isAdmittableEntry(entry)) {
    return 'Add to current call';
  }
  return getWaitingRoomEntrySublabel(entry) ?? 'Already admitted';
};

const normalizeParticipantName = (name: string) => name.trim().toLowerCase();

export function PipView({
  dailyCallService,
  audioInputDeviceService,
  setSelectedMicrophone,
  pipWindow,
  isRecording,
  isRecordingStarting,
  isRecordingPaused = false,
  session,
  onLeave,
  onClose,
  isPipWindow = false,
  onEnterPip,
  isPipSupported = false,
  onPauseRecording,
  onResumeRecording,
  onToggleFullscreen,
  isFullscreen = false,
  compactControls = false,
  popoverContainerEl,
}: PipViewProps) {
  const callState = useDailyCallState(dailyCallService);
  const remoteParticipantIds = useParticipantIds({ filter: 'remote' });
  const localSessionId = useLocalSessionId();
  const localUserName = useParticipantProperty(localSessionId ?? '', 'user_name');
  const hideSelfView = useAppSelector(selectHideSelfView);
  const layout = useCallLayout(hideSelfView);
  const { telehealth } = useEventTracking();
  const { enqueueSnackbar } = useSnackbar();
  const subjectLanguage = useSubjectLanguageVariants();
  const { isSharingScreen, screens, startScreenShare, stopScreenShare } = useScreenShare();

  const { sendChatMessage, waitingRoom } = useTelehealthCallManager();
  const dispatch = useAppDispatch();
  const chatMessages = useAppSelector(selectChatMessages);
  const chatUnreadCount = useAppSelector(selectChatUnreadCount);
  const isChatPanelOpen = useAppSelector(selectIsChatPanelOpen);
  const waitingRoomLaunchIntent = useAppSelector(selectWaitingRoomLaunchIntent);

  const [activeDropdown, setActiveDropdown] = useState<'mic' | 'camera' | null>(null);
  const [isPeoplePanelOpen, setIsPeoplePanelOpen] = useState(false);
  const [isCopyingWaitingRoomLink, setIsCopyingWaitingRoomLink] = useState(false);
  const [waitingRoomLinkCopied, setWaitingRoomLinkCopied] = useState(false);
  const [entryPendingRemoval, setEntryPendingRemoval] = useState<WaitingRoomEntry | null>(null);
  const [entryPendingGuardedAdmit, setEntryPendingGuardedAdmit] = useState<WaitingRoomEntry | null>(
    null,
  );
  const hasAutoOpenedPeoplePanelRef = useRef(false);
  const hasAttemptedAutoAvailabilityRef = useRef(false);
  const someoneElseSharing = screens.length > 0 && !isSharingScreen;
  const {
    state: roomLifecycleGuardState,
    isGuardedEmptyRoom,
    shouldShowEmptyRoomNudge,
    lastRemoteParticipantNames,
    markWaitingForReconnect,
  } = useTelehealthCallLifecycleGuard({
    remoteParticipantIds,
    remoteParticipantNames: callState.remoteParticipantNames,
  });

  // Responsive button sizing
  const btnSize = compactControls ? 36 : 48;
  const iconSize = compactControls ? 16 : 20;
  const waitingRoomEntries = waitingRoom.entries.filter(isWaitingRoomListEntry);
  const inCallWaitingRoomEntries = waitingRoom.entries.filter(isInCallWaitingRoomEntry);
  const waitingCount = waitingRoomEntries.length;
  const providerAvailability = waitingRoom.providerAvailability;
  const isSettingAvailability = waitingRoom.isSettingAvailability;
  const setWaitingRoomAvailability = waitingRoom.setAvailability;
  const defaultExpandedWaitingRoomEntryId =
    waitingRoomLaunchIntent && waitingRoomLaunchIntent.encounterId === session.encounterId
      ? waitingRoomLaunchIntent.entryId
      : undefined;
  const shouldShowWaitingRoomControls = waitingRoom.hasAccess;
  const canShowPeopleRail = shouldShowWaitingRoomControls && isPeoplePanelOpen && !compactControls;
  const canShowPeopleOverlay =
    shouldShowWaitingRoomControls && isPeoplePanelOpen && compactControls;
  const waitingRoomPopoverContainerEl = pipWindow?.document.body ?? popoverContainerEl ?? null;
  const inCallParticipants = remoteParticipantIds.map((participantId, index) => {
    const dailyParticipantName = callState.remoteParticipantNames.get(participantId);
    const participantName = dailyParticipantName ?? `Participant ${index + 1}`;
    const matchingEntries = inCallWaitingRoomEntries.filter(
      entry =>
        normalizeParticipantName(entry.patient_name) === normalizeParticipantName(participantName),
    );
    const matchedEntry = matchingEntries.length === 1 ? matchingEntries[0] : undefined;

    return {
      id: participantId,
      name: participantName,
      subtitle: 'In call',
      queueEntryId: matchedEntry?.waiting_room_queue_entry_id,
    };
  });
  const previousParticipantLabel =
    formatTelehealthParticipantList(lastRemoteParticipantNames) ??
    `the previous ${subjectLanguage.singular}`;
  const isWaitingForReconnect = roomLifecycleGuardState === 'empty_kept_open';

  useEffect(() => {
    if (!shouldShowWaitingRoomControls) {
      return;
    }

    // The PiP window is too small to auto-pop the panel — the provider opts
    // in explicitly via the People button. Auto-set-online still runs below.
    if (isPipWindow) {
      return;
    }

    if (waitingCount === 0) {
      hasAutoOpenedPeoplePanelRef.current = false;
      return;
    }

    if (!hasAutoOpenedPeoplePanelRef.current && !compactControls) {
      hasAutoOpenedPeoplePanelRef.current = true;
      telehealth.waitingRoomPanelOpened({
        source: 'in_call_auto',
        waiting_count: waitingCount,
        provider_availability: providerAvailability,
      });
      setIsPeoplePanelOpen(true);
    }
  }, [
    compactControls,
    isPipWindow,
    providerAvailability,
    shouldShowWaitingRoomControls,
    telehealth,
    waitingCount,
  ]);

  useEffect(() => {
    if (!isPeoplePanelOpen || providerAvailability === 'online') {
      hasAttemptedAutoAvailabilityRef.current = false;
    }

    if (
      !shouldShowWaitingRoomControls ||
      !isPeoplePanelOpen ||
      providerAvailability === 'online' ||
      isSettingAvailability ||
      hasAttemptedAutoAvailabilityRef.current
    ) {
      return;
    }

    hasAttemptedAutoAvailabilityRef.current = true;
    void setWaitingRoomAvailability('online', { source: 'in_call_auto' }).catch(() => {
      enqueueSnackbar('Could not update waiting room availability.', { variant: 'error' });
    });
  }, [
    enqueueSnackbar,
    isPeoplePanelOpen,
    isSettingAvailability,
    providerAvailability,
    setWaitingRoomAvailability,
    shouldShowWaitingRoomControls,
  ]);

  const handleCopyWaitingRoomLink = useCallback(async () => {
    const metadata = {
      source: isPipWindow ? 'pip_panel' : 'in_call_panel',
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId,
      waiting_count: waitingCount,
      provider_availability: providerAvailability,
    };
    const shortUrl = waitingRoom.link?.short_url;
    if (!shortUrl) {
      telehealth.waitingRoomLinkCopyAttempted({
        ...metadata,
        outcome: 'unavailable',
        has_link: false,
      });
      enqueueSnackbar('Waiting room link is not available yet.', { variant: 'warning' });
      return;
    }

    setIsCopyingWaitingRoomLink(true);
    try {
      await navigator.clipboard.writeText(shortUrl);
      telehealth.waitingRoomLinkCopyAttempted({
        ...metadata,
        outcome: 'success',
        has_link: true,
      });
      telehealth.linkCopied(metadata);
      setWaitingRoomLinkCopied(true);
      enqueueSnackbar('Waiting room link copied.', { variant: 'success' });
      window.setTimeout(() => setWaitingRoomLinkCopied(false), 1500);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Clipboard copy failed';
      telehealth.waitingRoomLinkCopyAttempted({
        ...metadata,
        outcome: 'failed',
        has_link: true,
        error_message: errorMessage,
      });
      telehealth.linkCopyFailed({
        appointment_id: session.appointmentId,
        error_message: errorMessage,
      });
      enqueueSnackbar('Could not copy waiting room link.', { variant: 'error' });
    } finally {
      setIsCopyingWaitingRoomLink(false);
    }
  }, [
    enqueueSnackbar,
    isPipWindow,
    providerAvailability,
    session.appointmentId,
    session.encounterId,
    telehealth,
    waitingCount,
    waitingRoom.link?.short_url,
  ]);

  const admitWaitingRoomEntry = useCallback(
    async (entry: WaitingRoomEntry) => {
      if (!session.encounterId) {
        enqueueSnackbar(`Open an encounter before admitting a ${subjectLanguage.singular}.`, {
          variant: 'warning',
        });
        return;
      }

      try {
        await waitingRoom.admitPatient(entry.waiting_room_queue_entry_id, session.encounterId);
        dispatch(clearWaitingRoomLaunchIntent());
        enqueueSnackbar(`${entry.patient_name} was added to the call.`, { variant: 'success' });
      } catch (error) {
        SentryService.captureException(error, {
          tags: { component: 'telehealth_waiting_room_in_call_admit' },
        });
        await waitingRoom.refreshQueue().catch(() => undefined);
        enqueueSnackbar(getWaitingRoomAdmitErrorMessage(error, subjectLanguage), {
          variant: 'error',
        });
      }
    },
    [dispatch, enqueueSnackbar, session.encounterId, subjectLanguage, waitingRoom],
  );

  const handleAdmitWaitingRoomEntry = useCallback(
    async (entry: WaitingRoomEntry) => {
      if (isGuardedEmptyRoom) {
        setEntryPendingGuardedAdmit(entry);
        SentryService.addBreadcrumb({
          category: 'telehealth.waiting_room',
          level: 'info',
          message: 'Waiting-room admit paused because the active call is empty',
          data: {
            encounterId: session.encounterId,
            entryId: entry.waiting_room_queue_entry_id,
            guardState: roomLifecycleGuardState,
          },
        });
        return;
      }

      await admitWaitingRoomEntry(entry);
    },
    [admitWaitingRoomEntry, isGuardedEmptyRoom, roomLifecycleGuardState, session.encounterId],
  );

  const handleConfirmGuardedAdmit = useCallback(async () => {
    if (!entryPendingGuardedAdmit) {
      return;
    }

    const entry = entryPendingGuardedAdmit;
    setEntryPendingGuardedAdmit(null);
    await admitWaitingRoomEntry(entry);
  }, [admitWaitingRoomEntry, entryPendingGuardedAdmit]);

  const handleKeepWaitingForReconnect = useCallback(() => {
    setEntryPendingGuardedAdmit(null);
    markWaitingForReconnect();
  }, [markWaitingForReconnect]);

  const handleEndCallFromGuard = useCallback(() => {
    setEntryPendingGuardedAdmit(null);
    onLeave();
  }, [onLeave]);

  const requestRemoveWaitingRoomEntry = useCallback(
    (entry: WaitingRoomEntry) => {
      telehealth.waitingRoomRemoveRequested({
        entry_id: entry.waiting_room_queue_entry_id,
        entry_state: entry.current_state,
        source: isPipWindow ? 'pip_panel' : 'in_call_panel',
        appointment_id: session.appointmentId,
        encounter_id: session.encounterId,
        waiting_count: waitingCount,
      });
      setEntryPendingRemoval(entry);
    },
    [isPipWindow, session.appointmentId, session.encounterId, telehealth, waitingCount],
  );

  const handlePeoplePanelToggle = useCallback(() => {
    const nextOpen = !isPeoplePanelOpen;
    const metadata = {
      source: isPipWindow ? 'pip_panel' : 'in_call_panel',
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId,
      waiting_count: waitingCount,
      provider_availability: providerAvailability,
    };

    if (nextOpen) {
      telehealth.waitingRoomPanelOpened(metadata);
    } else {
      telehealth.waitingRoomPanelClosed(metadata);
    }

    setIsPeoplePanelOpen(nextOpen);
  }, [
    isPeoplePanelOpen,
    isPipWindow,
    providerAvailability,
    session.appointmentId,
    session.encounterId,
    telehealth,
    waitingCount,
  ]);

  const handlePeoplePanelClose = useCallback(() => {
    telehealth.waitingRoomPanelClosed({
      source: isPipWindow ? 'pip_panel' : 'in_call_panel',
      appointment_id: session.appointmentId,
      encounter_id: session.encounterId,
      waiting_count: waitingCount,
      provider_availability: providerAvailability,
    });
    setIsPeoplePanelOpen(false);
  }, [
    isPipWindow,
    providerAvailability,
    session.appointmentId,
    session.encounterId,
    telehealth,
    waitingCount,
  ]);

  const handleConfirmRemoveWaitingRoomEntry = useCallback(
    async (request: ProviderWaitingRoomRemoveRequest) => {
      if (!entryPendingRemoval) {
        return;
      }

      try {
        await waitingRoom.removeQueueEntry(
          entryPendingRemoval.waiting_room_queue_entry_id,
          request,
        );
        enqueueSnackbar(`${entryPendingRemoval.patient_name} was removed from the waiting room.`, {
          variant: 'success',
        });
        setEntryPendingRemoval(null);
      } catch (error) {
        SentryService.captureException(error, {
          tags: { component: 'telehealth_waiting_room_in_call_remove' },
        });
        await waitingRoom.refreshQueue().catch(() => undefined);
        enqueueSnackbar(getWaitingRoomRemoveErrorMessage(error, subjectLanguage), {
          variant: 'error',
        });
      }
    },
    [enqueueSnackbar, entryPendingRemoval, subjectLanguage, waitingRoom],
  );

  const handleRemoveInCallParticipant = useCallback(
    (participant: { id: string; name: string }) => {
      try {
        dailyCallService.ejectParticipant(participant.id);
        enqueueSnackbar(`${participant.name} was removed.`, {
          variant: 'success',
        });
      } catch (error) {
        SentryService.captureException(error, {
          tags: { component: 'telehealth_waiting_room_in_call_participant_remove' },
        });
        enqueueSnackbar('Could not remove this participant.', {
          variant: 'error',
        });
      }
    },
    [dailyCallService, enqueueSnackbar],
  );

  const waitingRoomPanel = shouldShowWaitingRoomControls ? (
    <WaitingRoomPeoplePanel
      entries={waitingRoomEntries}
      providerAvailability={waitingRoom.providerAvailability}
      sseConnectionState={waitingRoom.sseConnectionState}
      link={waitingRoom.link}
      error={waitingRoom.error}
      inCallParticipants={inCallParticipants}
      variant="dark"
      density="compact"
      isSettingAvailability={waitingRoom.isSettingAvailability}
      isCopyingLink={isCopyingWaitingRoomLink}
      linkCopied={waitingRoomLinkCopied}
      showAvailabilityToggle={false}
      alwaysOpen={waitingRoom.alwaysOpen}
      defaultExpandedEntryId={defaultExpandedWaitingRoomEntryId}
      popoverContainerEl={waitingRoomPopoverContainerEl}
      onRemoveInCallParticipant={handleRemoveInCallParticipant}
      entryExpandedContent={entry => (
        <WaitingRoomEntryActionButtons
          variant="dark"
          primaryLabel={getAdmitActionLabel(entry)}
          primaryIcon={<VideocamIcon fontSize="small" />}
          onPrimaryAction={
            waitingRoom.isAdmitting || !isAdmittableEntry(entry)
              ? undefined
              : () => void handleAdmitWaitingRoomEntry(entry)
          }
          onRemoveFromWaitingRoom={
            waitingRoom.isRemoving ? undefined : () => requestRemoveWaitingRoomEntry(entry)
          }
        />
      )}
      onCopyLink={handleCopyWaitingRoomLink}
      onClose={handlePeoplePanelClose}
    />
  ) : null;

  const handleToggleMic = useCallback(() => {
    const newState = !callState.localAudioEnabled;
    dailyCallService.setLocalAudio(newState);
    telehealth.micToggled({
      appointment_id: session.appointmentId,
      enabled: newState,
    });
  }, [callState.localAudioEnabled, dailyCallService, session.appointmentId, telehealth]);

  const handleToggleVideo = useCallback(() => {
    const newState = !callState.localVideoEnabled;
    dailyCallService.setLocalVideo(newState);
    telehealth.videoToggled({
      appointment_id: session.appointmentId,
      enabled: newState,
    });
  }, [callState.localVideoEnabled, dailyCallService, session.appointmentId, telehealth]);

  const handleShareToggle = useCallback(() => {
    if (isSharingScreen) {
      stopScreenShare();
    } else {
      startScreenShare();
    }
    telehealth.screenShareToggled({
      appointment_id: session.appointmentId,
      enabled: !isSharingScreen,
    });
  }, [isSharingScreen, startScreenShare, stopScreenShare, session.appointmentId, telehealth]);

  // Tile-click affordance on the local screen-share preview reuses the toggle so the
  // existing screenShareToggled analytics event fires with enabled=false. Guard against
  // a race where the toolbar already stopped the share mid-click.
  const handleStopLocalScreenShareFromTile = useCallback(() => {
    if (!isSharingScreen) return;
    handleShareToggle();
  }, [handleShareToggle, isSharingScreen]);

  const handleLayoutChange = useCallback(
    (choice: ManualLayoutChoice) => {
      layout.setManualLayoutChoice(choice);
      telehealth.layoutChanged({
        appointment_id: session.appointmentId,
        layout: choice,
        participant_count: callState.participantCount,
      });
    },
    [layout, session.appointmentId, callState.participantCount, telehealth],
  );

  const handleSendChat = useCallback(
    (text: string) => {
      sendChatMessage({
        type: 'chat',
        id: crypto.randomUUID(),
        text,
        senderName: localUserName || 'Provider',
        senderRole: 'provider',
        senderId: localSessionId ?? undefined,
        timestamp: Date.now(),
      });
    },
    [sendChatMessage, localUserName, localSessionId],
  );

  const showStatusRow = isSharingScreen || isRecording || shouldShowEmptyRoomNudge;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: '100%',
        bgcolor: 'grey.700',
        overflow: 'hidden',
        gap: 1.5,
        p: 1.5,
      }}
    >
      <WaitingRoomRemoveDialog
        open={Boolean(entryPendingRemoval)}
        entry={entryPendingRemoval}
        isSubmitting={waitingRoom.isRemoving}
        container={waitingRoomPopoverContainerEl ?? undefined}
        onClose={() => setEntryPendingRemoval(null)}
        onConfirm={handleConfirmRemoveWaitingRoomEntry}
      />
      <Dialog
        open={Boolean(entryPendingGuardedAdmit)}
        container={waitingRoomPopoverContainerEl ?? undefined}
        onClose={() => setEntryPendingGuardedAdmit(null)}
      >
        <DialogTitle>This call is still open</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You are still in the call for {previousParticipantLabel}. End this call before starting
            with {entryPendingGuardedAdmit?.patient_name}, or add them to this encounter
            intentionally.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleKeepWaitingForReconnect}>Keep waiting</Button>
          <Button onClick={handleEndCallFromGuard}>End call</Button>
          <Button variant="contained" onClick={() => void handleConfirmGuardedAdmit()}>
            Add to this call
          </Button>
        </DialogActions>
      </Dialog>

      {/* Status row — recording indicator first, then screen-share indicator beneath.
          The screen-share row carries its own right-aligned Stop sharing button so the
          provider can stop without hunting for the toolbar control. */}
      {showStatusRow && (
        <Stack direction="column" spacing={1}>
          {(isRecording || isRecordingStarting) && onPauseRecording && onResumeRecording && (
            <RecordingStatusBar
              isRecording={isRecording}
              isStarting={isRecordingStarting}
              isPaused={isRecordingPaused}
              onPause={onPauseRecording}
              onResume={onResumeRecording}
              hasRemoteParticipant={remoteParticipantIds.length > 0}
            />
          )}

          {isSharingScreen && (
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                bgcolor: 'grey.800',
                px: 2,
                py: 0.75,
                borderRadius: 2,
              }}
            >
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
                <ScreenShareIcon sx={{ fontSize: 20, color: 'common.white' }} />
                <Typography sx={{ color: 'common.white', fontSize: 12 }}>
                  You are sharing your screen
                </Typography>
              </Stack>
              <ButtonBase
                onClick={handleShareToggle}
                aria-label="Stop sharing your screen"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  color: 'error.light',
                  flexShrink: 0,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                  '&:focus-visible': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <StopScreenShareIcon sx={{ fontSize: 16 }} />
                <Typography sx={{ fontSize: 12, fontWeight: 600 }}>Stop sharing</Typography>
              </ButtonBase>
            </Stack>
          )}
          {shouldShowEmptyRoomNudge && (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{
                alignItems: { xs: 'stretch', sm: 'center' },
                justifyContent: 'space-between',
                gap: 1,
                bgcolor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.12)',
                px: 2,
                py: 1,
                borderRadius: 2,
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: 'common.white', fontSize: 13, fontWeight: 700 }}>
                  {isWaitingForReconnect
                    ? `Waiting for ${previousParticipantLabel} to reconnect`
                    : `${previousParticipantLabel} left the call`}
                </Typography>
                <Typography sx={{ color: 'grey.300', fontSize: 12 }}>
                  Keep this room open if they may reconnect. End the call before starting with
                  another {subjectLanguage.singular}.
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                {!isWaitingForReconnect && (
                  <Button
                    size="small"
                    variant="outlined"
                    color="inherit"
                    onClick={handleKeepWaitingForReconnect}
                    sx={{
                      color: 'common.white',
                      borderColor: 'rgba(255,255,255,0.28)',
                      '&:hover': { borderColor: 'rgba(255,255,255,0.5)' },
                    }}
                  >
                    Wait here
                  </Button>
                )}
                <Button size="small" variant="contained" color="error" onClick={onLeave}>
                  End call
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      )}

      {/* Video + chat row */}
      <Box sx={{ display: 'flex', flex: 1, minHeight: 0, position: 'relative' }}>
        {canShowPeopleRail && waitingRoomPanel && (
          <Box
            sx={{
              width: 320,
              flexShrink: 0,
              minHeight: 0,
              mr: 1.5,
            }}
          >
            {waitingRoomPanel}
          </Box>
        )}

        {canShowPeopleOverlay && waitingRoomPanel && (
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              zIndex: 4,
              width: 'min(320px, calc(100% - 16px))',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.35)',
            }}
          >
            {waitingRoomPanel}
          </Box>
        )}

        {/* Layout area — same IIFE pattern as CallView */}
        <Box
          sx={{
            position: 'relative',
            borderRadius: '8px',
            overflow: 'hidden',
            flex: 1,
            minHeight: 0,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {(() => {
            if (layout.activeLayout === 'speaker') {
              return (
                <SpeakerView
                  mainStageSessionId={layout.mainStageSessionId}
                  sidebarParticipantIds={layout.sidebarParticipantIds}
                  activeSpeakerId={layout.activeSpeakerId}
                  localSessionId={layout.localSessionId}
                  isScreenShare={layout.hasRemoteScreenShare}
                  screenShareSessionId={layout.screenShareSessionId}
                  onStopLocalScreenShare={handleStopLocalScreenShareFromTile}
                  fillHeight
                />
              );
            }
            if (layout.activeLayout === 'grid') {
              return (
                <GridView
                  participantIds={layout.sidebarParticipantIds}
                  activeSpeakerId={layout.activeSpeakerId}
                  localSessionId={layout.localSessionId}
                  onStopLocalScreenShare={handleStopLocalScreenShareFromTile}
                  fillHeight
                />
              );
            }
            // spotlight (default for ≤2 participants)
            return (
              <VideoSpotlight
                remoteParticipantIds={remoteParticipantIds}
                localSessionId={localSessionId ?? ''}
                activeSpeakerId={layout.activeSpeakerId}
                fillHeight
                showLocalThumbnail={!hideSelfView}
                localScreenShareSessionId={layout.localScreenShareSessionId}
                onStopLocalScreenShare={handleStopLocalScreenShareFromTile}
              />
            );
          })()}
        </Box>

        {/* Chat panel sidebar */}
        <Box
          sx={{
            width: isChatPanelOpen ? 332 : 0,
            overflow: 'hidden',
            transition: 'width 200ms ease',
            flexShrink: 0,
            paddingLeft: isChatPanelOpen ? 1.5 : 0,
          }}
        >
          {isChatPanelOpen && (
            <ChatPanel
              messages={chatMessages}
              onSend={handleSendChat}
              onClose={() => dispatch(setChatPanelOpen(false))}
              isConnected={remoteParticipantIds.length > 0}
              localSessionId={localSessionId}
            />
          )}
        </Box>
      </Box>

      {/* Controls bar — 3-column grid matching CallView */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: '1fr auto 1fr',
          alignItems: 'center',
        }}
      >
        {/* Left group: return to encounter + layout toggle */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', minWidth: 0, justifySelf: 'start' }}
        >
          {shouldShowWaitingRoomControls && (
            <Tooltip title={isPeoplePanelOpen ? 'Hide people' : 'Show people'}>
              <ButtonBase
                onClick={handlePeoplePanelToggle}
                aria-label={isPeoplePanelOpen ? 'Hide people panel' : 'Show people panel'}
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  bgcolor: isPeoplePanelOpen ? 'primary.main' : undefined,
                  '&:hover': {
                    bgcolor: isPeoplePanelOpen ? 'primary.dark' : 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <Badge
                  badgeContent={waitingCount}
                  color="primary"
                  max={99}
                  invisible={isPeoplePanelOpen || waitingCount === 0}
                >
                  <PeopleIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
                </Badge>
              </ButtonBase>
            </Tooltip>
          )}

          {!isPipWindow && onClose && (
            <Tooltip title="Return to encounter">
              <ButtonBase
                onClick={onClose}
                aria-label="Return to encounter"
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <ArrowBackIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          )}
          {layout.showLayoutToggle && (
            <LayoutToggle layout={layout.manualLayoutChoice} onChange={handleLayoutChange} />
          )}
        </Stack>

        {/* Center group: mic, camera, share, end call */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'stretch',
            gap: compactControls ? 1 : 1.5,
            padding: compactControls ? 0.75 : 1,
            borderRadius: 2,
            backgroundColor: 'grey.800',
          }}
        >
          {/* Mic split button */}
          <SplitMediaButton
            compact={compactControls}
            icon={<MicIcon sx={{ color: 'common.white', fontSize: iconSize }} />}
            inactiveIcon={<MicOffIcon color="error" sx={{ fontSize: iconSize }} />}
            isActive={callState.localAudioEnabled}
            onToggle={handleToggleMic}
            ariaLabel={callState.localAudioEnabled ? 'Mute microphone' : 'Unmute microphone'}
            tooltip={callState.localAudioEnabled ? 'Mute' : 'Unmute'}
            isDropdownOpen={activeDropdown === 'mic'}
            onDropdownOpenChange={open => setActiveDropdown(open ? 'mic' : null)}
            popoverContainer={pipWindow?.document.body ?? popoverContainerEl ?? undefined}
            dropdownContent={
              <MicDropdown
                audioInputDeviceService={audioInputDeviceService}
                isRecording={isRecording}
                session={session}
                setSelectedMicrophone={setSelectedMicrophone}
                onClose={() => setActiveDropdown(null)}
              />
            }
          />

          {/* Camera split button */}
          <SplitMediaButton
            compact={compactControls}
            icon={<VideocamIcon sx={{ color: 'common.white', fontSize: iconSize }} />}
            inactiveIcon={<VideocamOffIcon color="error" sx={{ fontSize: iconSize }} />}
            isActive={callState.localVideoEnabled}
            onToggle={handleToggleVideo}
            ariaLabel={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            tooltip={callState.localVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
            isDropdownOpen={activeDropdown === 'camera'}
            onDropdownOpenChange={open => setActiveDropdown(open ? 'camera' : null)}
            popoverContainer={pipWindow?.document.body ?? popoverContainerEl ?? undefined}
            dropdownContent={
              <CameraDropdown
                isRecording={isRecording}
                session={session}
                onClose={() => setActiveDropdown(null)}
              />
            }
          />

          {/* Screen share toggle */}
          <Tooltip
            title={
              someoneElseSharing
                ? 'Another participant is sharing'
                : isSharingScreen
                  ? 'Stop sharing'
                  : 'Share screen'
            }
          >
            <ButtonBase
              onClick={handleShareToggle}
              aria-label={isSharingScreen ? 'Stop screen share' : 'Share screen'}
              disabled={someoneElseSharing}
              sx={{
                width: btnSize,
                height: btnSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {isSharingScreen ? (
                <StopScreenShareIcon sx={{ fontSize: iconSize, color: 'error.main' }} />
              ) : (
                <ScreenShareIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              )}
            </ButtonBase>
          </Tooltip>

          {/* Chat toggle */}
          <Tooltip title={isChatPanelOpen ? 'Close chat' : 'Chat'}>
            <ButtonBase
              onClick={() => dispatch(toggleChatPanel())}
              aria-label={isChatPanelOpen ? 'Close chat' : 'Open chat'}
              sx={{
                width: btnSize,
                height: btnSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 1,
                bgcolor: isChatPanelOpen ? 'primary.main' : undefined,
                '&:hover': {
                  bgcolor: isChatPanelOpen ? 'primary.dark' : 'rgba(255,255,255,0.1)',
                },
              }}
            >
              <Badge
                badgeContent={chatUnreadCount}
                color="error"
                max={99}
                invisible={chatUnreadCount === 0 || isChatPanelOpen}
              >
                <ChatIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </Badge>
            </ButtonBase>
          </Tooltip>

          <Tooltip title="Leave call">
            <ButtonBase
              onClick={onLeave}
              aria-label="Leave call"
              sx={{
                width: btnSize,
                height: btnSize,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'error.main',
                color: 'common.white',
                borderRadius: 1,
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              <LogoutIcon sx={{ fontSize: iconSize }} />
            </ButtonBase>
          </Tooltip>
        </Box>

        {/* Right group: fullscreen + PiP */}
        <Stack
          direction="row"
          spacing={1}
          sx={{ alignItems: 'center', minWidth: 0, justifySelf: 'end' }}
        >
          {!isPipWindow && onToggleFullscreen && (
            <Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              <ButtonBase
                onClick={onToggleFullscreen}
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                {isFullscreen ? (
                  <FullscreenExitIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
                ) : (
                  <FullscreenIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
                )}
              </ButtonBase>
            </Tooltip>
          )}
          {isPipWindow ? (
            <Tooltip title="Close picture-in-picture">
              <ButtonBase
                onClick={onClose}
                aria-label="Close picture-in-picture"
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <PipExitIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          ) : isPipSupported && onEnterPip ? (
            <Tooltip title="Picture-in-picture">
              <ButtonBase
                onClick={onEnterPip}
                aria-label="Picture-in-picture"
                sx={{
                  width: btnSize,
                  height: btnSize,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
                }}
              >
                <PipIcon sx={{ fontSize: iconSize, color: 'common.white' }} />
              </ButtonBase>
            </Tooltip>
          ) : null}
        </Stack>
      </Box>
    </Box>
  );
}
